import { formatCustomValue } from '@/components/documents/custom-field-format';
import { ApiError, apiErrorReason } from '@/lib/api';

// Formulario de los campos propios de un documento (QA F1 #14), puro: del valor al
// texto editable y de vuelta, validación por tipo con la regla del API (el API sigue
// siendo la autoridad), el cambio mínimo a enviar y los rechazos en lenguaje claro.

export interface CustomFieldItem {
  key: string;
  label: string;
  dataType: string;
  options: string[] | null;
  required: boolean;
  value: unknown;
}

export type FieldValue = string | number | boolean;
const TEXT_MAX = 2000;
const NUMBER = /^-?\d+(?:[.,]\d+)?$/;

/** Valor guardado → texto del control ('' = sin valor; sí/no como "si"/"no"). */
export function toFormValue(field: CustomFieldItem): string {
  const v = field.value;
  if (v === null || v === undefined) {
    return '';
  }
  if (field.dataType === 'boolean') {
    return v === true ? 'si' : v === false ? 'no' : '';
  }
  if (field.dataType === 'number' && typeof v === 'number') {
    // Mismo formato que la ficha: coma decimal, sin miles ni notación científica.
    return formatCustomValue('number', v);
  }
  return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}

/** Etiqueta del control: un obligatorio se nombra "(obligatorio)", como en el resto. */
export function fieldLabel(field: CustomFieldItem): string {
  return field.required ? `${field.label} (obligatorio)` : field.label;
}

/** Texto del control → valor a enviar (null = vacío). */
function fromFormValue(field: CustomFieldItem, raw: string): FieldValue | null {
  const text = raw.trim();
  if (text === '') {
    return null;
  }
  if (field.dataType === 'number') {
    return Number(text.replace(',', '.'));
  }
  if (field.dataType === 'boolean') {
    return text === 'si';
  }
  return text;
}

export function fieldInputError(field: CustomFieldItem, raw: string): string | null {
  const text = raw.trim();
  if (text === '') {
    return field.required ? 'Completa este campo' : null;
  }
  if (field.dataType === 'number' && !NUMBER.test(text)) {
    return 'Escribe un número';
  }
  if (
    field.dataType === 'number' &&
    (text.replace(/\D/g, '').length > 15 || !Number.isFinite(Number(text.replace(',', '.'))))
  ) {
    return 'El número es demasiado grande';
  }
  if (field.dataType === 'text' && text.length > TEXT_MAX) {
    return `Admite hasta ${TEXT_MAX} caracteres`;
  }
  return null;
}

/** Solo los campos que cambiaron, con su tipo; vaciar uno envía null. */
export function toPatch(
  fields: readonly CustomFieldItem[],
  form: Readonly<Record<string, string>>,
): Record<string, FieldValue | null> {
  const patch: Record<string, FieldValue | null> = {};
  for (const field of fields) {
    const raw = form[field.key];
    if (raw === undefined) {
      continue;
    }
    const next = fromFormValue(field, raw);
    const current = field.value === undefined ? null : field.value;
    if (next !== current) {
      patch[field.key] = next;
    }
  }
  return patch;
}

export interface CustomFieldsErrorView {
  /** Error general del formulario; null si el error es de campos puntuales. */
  form: string | null;
  fields: Record<string, string>;
}

const FORM_REJECTIONS: Record<string, string> = {
  unknown_field: 'Uno de los campos fue retirado o cambió. Recarga la ficha e intenta de nuevo.',
  annulled_document: 'El documento está anulado y ya no admite cambios.',
};

export function customFieldsError(err: unknown): CustomFieldsErrorView {
  const reason = apiErrorReason(err);
  if (reason === 'invalid_custom_fields' && err instanceof ApiError) {
    const listed = err.meta?.['fields'];
    const keys = Array.isArray(listed)
      ? listed.flatMap((f) =>
          f !== null && typeof f === 'object' && typeof (f as { key?: unknown }).key === 'string'
            ? [(f as { key: string }).key]
            : [],
        )
      : [];
    return {
      form: keys.length === 0 ? 'Hay valores que no son válidos. Revísalos.' : null,
      fields: Object.fromEntries(keys.map((k) => [k, 'El valor no es válido para este campo'])),
    };
  }
  if (reason !== undefined && FORM_REJECTIONS[reason] !== undefined) {
    return { form: FORM_REJECTIONS[reason], fields: {} };
  }
  if (err instanceof ApiError && err.status === 404) {
    return {
      form: 'El documento ya no está disponible en esta empresa. Vuelve a la Lista Maestra.',
      fields: {},
    };
  }
  if (err instanceof ApiError && err.status === 403) {
    return {
      form: 'No tienes permiso para completar estos datos. Pide acceso al Administrador de Calidad.',
      fields: {},
    };
  }
  return { form: 'No se pudo guardar. Intenta de nuevo.', fields: {} };
}
